import { useEffect, useRef, useState } from 'react';
import { Arrow } from '../../../icons/SvgIcons';
import { DropdownProps } from './Dropdown.types';

function Dropdown({
  label,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  disabled = false,
  error,
  className = '',
  size = 'md',
  buttonClassName = 'h-[42px] px-[13px] py-[9px]',
  placeholderClassName,
  menuClassName = '',
  optionClassName = '',
  allOptions,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption =
    options.find((option) => option.value === value) ||
    allOptions?.find((option) => option.value === value);

  function handleToggle() {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function handleOptionClick(optionValue: string) {
    onChange(optionValue);
    setIsOpen(false);
  }

  return (
    <div
      className={`flex flex-col w-full gap-1.5 ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="text-gray-800 text-left font-inter font-medium text-sm leading-5">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`w-full flex gap-1 items-center justify-between rounded-md border font-inter text-left transition-all duration-200 shadow-sm outline-none focus:outline-none ${buttonClassName} ${
            size === 'sm'
              ? 'px-2.5 py-1.5 text-sm font-medium leading-5'
              : 'h-[42px] px-[13px] py-[9px] text-sm leading-5'
          } ${
            isOpen
              ? 'border-purple-700 ring-1 ring-purple-700'
              : 'border-gray-350'
          } ${
            disabled
              ? 'bg-gray-100 cursor-not-allowed border-gray-300 text-gray-550'
              : 'bg-white cursor-pointer'
          } ${error ? 'border-red-300' : ''}`}
        >
          <span
            className={`${
              size === 'sm' ? 'text-sm font-medium leading-5' : 'text-base'
            } ${
              selectedOption ? 'text-gray-800' : 'text-gray-550'
            } ${placeholderClassName}`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <div
            className={`transition-transform duration-300  ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
          >
            {Arrow('down')}
          </div>
        </button>

        {isOpen && (
          <div
            className={`absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg max-h-[180px] overflow-auto border border-gray-200 ${menuClassName}`}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  handleOptionClick(option.value);
                }}
                className={`w-full h-9 px-4 py-2 cursor-pointer font-inter font-normal text-sm leading-5 text-gray-800 hover:bg-gray-100 hover:text-gray-900 flex items-center text-left ${optionClassName}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <span className="text-red-500 font-inter font-normal text-sm">
          {error}
        </span>
      )}
    </div>
  );
}

export default Dropdown;
